import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Short region/country codes that should stay fully uppercase */
const UPPERCASE_TOKENS = new Set([
  // Country codes
  'ae', 'ar', 'asp', 'at', 'au', 'be', 'bg', 'bh', 'br', 'ca', 'ch',
  'cl', 'co', 'cr', 'cz', 'de', 'dk', 'ec', 'ee', 'eg', 'es',
  'fi', 'fr', 'gh', 'gr', 'hk', 'hr', 'hu', 'id', 'ie', 'il',
  'in', 'it', 'jp', 'ke', 'ko', 'kr', 'kw', 'lt', 'lv', 'mx',
  'my', 'ng', 'nl', 'no', 'nz', 'om', 'pa', 'pe', 'ph', 'pl',
  'pt', 'qa', 'rb', 'ro', 'sa', 'se', 'sg', 'si', 'sk', 'th',
  'tr', 'tw', 'uk', 'us', 'usa', 'uy', 'vn', 'za',
  // Region codes
  'anz', 'apac', 'dach', 'emea', 'eu', 'gcc', 'latam', 'mewa', 'nam',
]);

/** Words with specific casing that should not be title-cased */
const SPECIAL_CASING: Record<string, string> = {
  'pepsico': 'PepsiCo',
};

/**
 * Format a workspace/client ID for display.
 * - Splits on underscores
 * - Optionally strips trailing region suffix (e.g., _de, _global)
 * - Region/country codes → UPPERCASE (DE, MEWA, KO, US)
 * - Special casing words → exact form (PepsiCo)
 * - Everything else → Title Case (Red, Bull)
 */
export function formatClientName(id: string, stripSuffix = false): string {
  let name = id;
  if (stripSuffix) {
    name = name.replace(/_[a-z0-9]{2,8}$/, '');
  }
  return name
    .split('_')
    .map(w => {
      const lower = w.toLowerCase();
      if (SPECIAL_CASING[lower]) return SPECIAL_CASING[lower];
      if (UPPERCASE_TOKENS.has(lower)) return w.toUpperCase();
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(' ');
}

export function formatNumber(value: number): string {
  const absValue = Math.abs(value);
  if (absValue >= 1_000_000) {
    return (value / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (absValue >= 1_000) {
    return (value / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
  }
  return value.toLocaleString();
}
